import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, AlertCircle, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { fadeUp, staggerParent } from "@/lib/motion";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { user, login, initializing } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!initializing && user) navigate("/admin/dashboard", { replace: true });
  }, [user, initializing, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(
        formatApiError(err.response?.data?.detail) ||
          "Login failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12 bg-bone relative overflow-hidden"
      data-testid="admin-login-page"
    >
      <div className="absolute inset-0 dotted opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-lime-pop blur-3xl opacity-30 pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-coral blur-3xl opacity-25 pointer-events-none" />

      <motion.div
        initial="hidden"
        animate="show"
        variants={staggerParent}
        className="relative w-full max-w-md"
      >
        <motion.div variants={fadeUp} className="flex flex-col items-center mb-8">
          <LogoMark className="h-14 w-14" />
          <div className="mt-4 font-display text-2xl text-ink">Mavericks</div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-warm">
            PBX Solutions · Admin
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="rounded-3xl border border-ink bg-white p-8 sm:p-10 shadow-[0_30px_80px_-30px_rgba(10,10,10,0.25)]"
        >
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-coral">
            <Shield className="h-3.5 w-3.5" /> Admin console
          </div>
          <h1 className="mt-3 font-display text-4xl text-ink tracking-tight leading-tight">
            Sign in to continue.
          </h1>
          <p className="mt-2 text-sm text-muted-warm">
            Restricted access. Authorized personnel only.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <Label htmlFor="username" className="text-sm font-medium text-ink mb-2 block">
                Username
              </Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                className="bg-white border-ink text-ink focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-0 h-11 rounded-xl"
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-ink mb-2 block">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="bg-white border-ink text-ink focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-0 h-11 rounded-xl"
                data-testid="admin-password-input"
              />
            </div>

            {error && (
              <div
                className="flex items-start gap-2 text-sm text-coral bg-coral/10 border border-coral/40 rounded-xl px-4 py-3"
                data-testid="admin-login-error"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full btn-lime h-12 group text-base"
              data-testid="admin-login-submit-btn"
            >
              <Lock className="h-4 w-4" />
              {submitting ? "Signing in..." : "Sign In"}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </Button>
          </form>
        </motion.div>

        <motion.p variants={fadeUp} className="mt-6 text-center text-xs text-muted-warm font-mono">
          Mavericks PBX Solutions · Admin Console
        </motion.p>
      </motion.div>
    </div>
  );
}
